import { CheckIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Avatar } from "@heroui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../utils/toast"; 
import { createFighterManagerLink, getFightersWithoutActiveLink } from "../../../services/api";
import { ProfileDto, ProfileId } from "../../../types/types";

interface AddFightersModalProps {
    isOpen: boolean;
    onClose: () => void;
    username: string | undefined;
    onSuccess: () => void;
}

const AddFightersModal: React.FC<AddFightersModalProps> = ({ isOpen, onClose, username, onSuccess }) => {
    const [fighters, setFighters] = useState<ProfileDto[]>([]);
    const [selectedFighterIds, setSelectedFighterIds] = useState<ProfileId[]>([]);
    const [modalError, setModalError] = useState<string | null>(null);
    const { t } = useTranslation('publicProfile');

    useEffect(() => {
        if (isOpen) {
            fetchFighters();
        }
    }, [isOpen]);

    const fetchFighters = async () => {
        try {
            const data = await getFightersWithoutActiveLink();
            setFighters(data);
            setModalError(null);
        } catch (error) {
            setModalError(t("failed_load_fighters"));
        }
    };

    const handleAddFighters = async () => {
    if (selectedFighterIds.length === 0) {
        setModalError(t("please_select_fighter")); 
        return;
    }

    try {
        const dto = {
            fighterIds: selectedFighterIds,
        };

        await createFighterManagerLink(username!, dto); 
        setSelectedFighterIds([]);
        setModalError(null);
        toast.success(t('fighters_added_success'))
        onSuccess();
        onClose();
    } catch (error) {
        setModalError(t("failed_add_fighters"));
    }
};


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white w-full max-w-3xl mx-auto rounded-xl shadow-xl p-8 transition-all duration-300 max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center mb-6 flex-shrink-0">
                    <h2 className="text-2xl font-semibold text-gray-800">
                        {t("add_fighters")}
                    </h2>
                    <button
                        className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                        onClick={() => {
                            onClose();
                            setModalError(null);
                            setSelectedFighterIds([]);
                        }}
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>
                {modalError && (
                    <div className="p-4 mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 flex-shrink-0">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {modalError}
                        </div>
                    </div>
                )}
                <div className="space-y-4 flex-grow overflow-hidden flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 flex-shrink-0">
                        {t("select_fighters_count")}{selectedFighterIds.length}
                    </label>
                    <div className="flex-grow overflow-y-auto border rounded-lg bg-gray-50 shadow-sm">
                        {fighters.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                <p className="text-sm">{t("no_fighters_available")}</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-2 p-4">
                                {fighters.map((fighter) => {
                                    const isSelected = selectedFighterIds.includes(fighter.id);
                                    return (
                                        <div
                                            key={fighter.id}
                                            className={`relative rounded-lg border-2 transition-all cursor-pointer ${isSelected
                                                ? "border-blue-500 bg-blue-50"
                                                : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                                                   }`}
                                            onClick={() => {
                                                if (isSelected) {
                                                    setSelectedFighterIds(
                                                        selectedFighterIds.filter((id) => id !== fighter.id)
                                                    );
                                                } else {
                                                    setSelectedFighterIds([...selectedFighterIds, fighter.id]);
                                                }
                                            }}
                                        >
                                            <div className="flex items-center justify-between p-2">
                                                <div className="flex items-center gap-4">
                                                    <div className="relative">
                                                        <Avatar
                                                            src={fighter.profileImage}
                                                            alt={`${fighter.firstName} ${fighter.lastName}`}
                                                            size="md"
                                                            className="w-12 h-12"
                                                        />
                                                        {isSelected && (
                                                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                                                <CheckIcon className="w-4 h-4 text-white" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h3 className="font-medium text-gray-900">
                                                            {fighter.firstName} {fighter.lastName}
                                                        </h3>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {selectedFighterIds.length > 0 && (
                        <div className="flex-shrink-0 bg-blue-50 rounded-lg p-4 border border-blue-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                                        <span className="text-white text-sm font-medium">{selectedFighterIds.length}</span>
                                    </div>
                                    <span className="text-sm font-medium text-blue-800">
                                        {t("selected_fighters", { count: selectedFighterIds.length })}
                                    </span>
                                </div>
                                <button
                                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                    onClick={() => setSelectedFighterIds([])}
                                >
                                    {t("clear_all")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                <div className="mt-6 flex justify-end gap-3 flex-shrink-0">
                    <button
                        className="px-6 py-2.5 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                        onClick={() => {
                            onClose();
                            setModalError(null);
                            setSelectedFighterIds([]);
                        }}
                    >
                        {t("cancel")}
                    </button>
                    <button
                        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium flex items-center gap-2"
                        disabled={selectedFighterIds.length === 0}
                        onClick={handleAddFighters}
                    >
                        <PlusIcon className="w-4 h-4" />
                        {t("add_fighters_button")} {selectedFighterIds.length}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddFightersModal;
